import { autoUpdate, computePosition, flip, offset } from '@floating-ui/dom';
import { ReactRenderer } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import { PluginKey } from '@tiptap/pm/state';
import type { ComponentType } from 'react';
import type {
  SuggestionKeyDownProps,
  SuggestionOptions,
  SuggestionProps,
} from '@tiptap/suggestion';

type MentionItem = {
  id: string;
  label: string;
  trigger: string;
};

type MentionCommandProps = {
  editor: Editor;
  props: {
    id?: null | string;
    label?: null | string;
    mentionSuggestionChar?: string;
  };
  range: {
    from: number;
    to: number;
  };
};

type MentionListRef = {
  onKeyDown?: (props: { event: KeyboardEvent }) => boolean;
};

export const createMentionSuggestionConfig = ({
  getMentionItems,
  pluginKey,
  render,
  trigger,
}: {
  getMentionItems: (trigger: string, query: string) => MentionItem[];
  pluginKey: PluginKey;
  render: SuggestionOptions['render'];
  trigger: string;
}) => ({
  char: trigger,
  command: ({ editor, props, range }: MentionCommandProps) => {
    editor
      .chain()
      .focus()
      .insertContentAt(range, [
        {
          attrs: {
            id: props.id ?? '',
            label: props.label ?? '',
            mentionSuggestionChar: props.mentionSuggestionChar || trigger,
          },
          type: 'mention',
        },
        {
          text: ' ',
          type: 'text',
        },
      ])
      .run();
  },
  items: ({ query }: { query: string }) => getMentionItems(trigger, query),
  pluginKey,
  render,
});

export const createSuggestionRenderer = ({
  MentionList,
  styles,
  suggestions = {},
}: {
  MentionList: ComponentType;
  styles: Record<string, string>;
  suggestions?: Record<string, unknown>;
}) => {
  return () => {
    let component: ReactRenderer | null = null;
    let popup: HTMLDivElement | null = null;
    let cleanupAutoUpdate: (() => void) | null = null;
    let removeBlurHandler: (() => void) | null = null;

    const cleanup = () => {
      if (cleanupAutoUpdate) {
        cleanupAutoUpdate();
        cleanupAutoUpdate = null;
      }
      if (removeBlurHandler) {
        removeBlurHandler();
        removeBlurHandler = null;
      }
      if (component) {
        component.destroy();
        component = null;
      }

      if (popup) {
        popup.remove();
        popup = null;
      }
    };

    const updatePosition = async (clientRect?: (() => DOMRect | null) | null, editor?: Editor) => {
      if (!popup || !clientRect) return;

      if (editor && !editor.isFocused) {
        cleanup();
        return;
      }

      const rect = clientRect();
      if (!rect) return;

      const editorRect = editor?.view?.dom?.getBoundingClientRect();
      if (!editorRect) return;

      popup.style.maxWidth = `${editorRect.width}px`;

      const reference = {
        contextElement: editor?.view?.dom || undefined,
        getBoundingClientRect: () => rect,
      };

      const { x, y } = await computePosition(reference, popup, {
        middleware: [offset(6), flip()],
        placement: 'bottom-start',
        strategy: 'fixed',
      });

      if (!popup) return;

      popup.style.left = `${x}px`;
      popup.style.top = `${y}px`;
    };

    return {
      onExit: () => {
        cleanup();
      },

      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (props.event.key === 'Escape') {
          cleanup();
          return true;
        }

        const ref = component?.ref as MentionListRef | null;
        return ref?.onKeyDown?.(props) || false;
      },

      onStart: (props: SuggestionProps) => {
        cleanup();

        component = new ReactRenderer(MentionList, {
          editor: props.editor,
          props: {
            ...props,
            suggestions,
          },
        });

        popup = document.createElement('div');
        popup.className = styles['rich-text-input-mention-list-popup'];
        popup.style.position = 'fixed';
        popup.style.zIndex = '9999';
        popup.appendChild(component.element);
        document.body.appendChild(popup);

        const handleBlur = () => {
          cleanup();
        };

        props.editor.on('blur', handleBlur);
        removeBlurHandler = () => props.editor.off('blur', handleBlur);

        void updatePosition(props.clientRect, props.editor);

        const reference = {
          contextElement: props.editor.view.dom,
          getBoundingClientRect: () => props.clientRect?.() || new DOMRect(),
        };

        cleanupAutoUpdate = autoUpdate(reference, popup, () => {
          void updatePosition(props.clientRect, props.editor);
        });
      },

      onUpdate: (props: SuggestionProps) => {
        if (!props.editor.isFocused) {
          cleanup();
          return;
        }

        if (!component) return;

        component.updateProps({
          ...props,
          suggestions,
        });

        void updatePosition(props.clientRect, props.editor);
      },
    };
  };
};
